import { useEffect, useState } from "react";
import InfiniteScroll from "../../../components/ui/InfiniteScroll";
import {
  getInboxMessages,
  readInboxMessage,
  deleteInboxMessage,
  readAllInboxMessages,
} from "../services/inbox.service";
import {
  InboxMessageDetails,
  InboxMessageResponse,
} from "../models/response/InboxMessageResponse";
import localization from "../../../config/localization";
import { useLanguage } from "../../../config/localization/LanguageProvider";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { formatUtcToLocalDateTime } from "../../../utils/dateTimeHelper";
import { useAppDispatch } from "../../../store/hooks";
import { markReadAllInboxMessages, markReadInboxMessage } from "../../users/reducers/userProfileReducer";
import { Button } from "../../../components/ui/Button";
import { changeLoaderState } from "../../../reducers/appStateReducer";

export default function InboxList() {
  const { language } = useLanguage();

  const PAGE_SIZE = 20;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InboxMessageResponse | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const isHindi = language === "hi";

  const dispatch = useAppDispatch();

  const decodeHtml = (html?: string) => {
    if (!html) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  useEffect(() => {
    dispatch(changeLoaderState(true));
  }, []);

  useEffect(() => {
    fetchData(true).finally(() => { setLoading(false); dispatch(changeLoaderState(false)); });
  }, [language]);

  const fetchData = async (reset = false) => {
    if (isLoadingMore && !reset) return;

    setIsLoadingMore(true);
    const page = reset ? 0 : pageNo;

    try {
      const res = await getInboxMessages({
        pageNo: page,
        pageSize: PAGE_SIZE,
      });

      if (res) {
        setData((prev) =>
          reset || !prev
            ? res
            : { details: [...prev.details, ...res.details] }
        );

        if (res.details.length < PAGE_SIZE) setHasMore(false);
        setPageNo((p) => p + 1);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleExpand = async (item: InboxMessageDetails) => {
    setExpandedId((prev) => (prev === item.id ? null : item.id));

    if (!item.isread) {
      await readInboxMessage({ id: item.id });
      dispatch(markReadInboxMessage());
      setData((prev) =>
        prev
          ? {
            details: prev.details.map((m) =>
              m.id === item.id ? { ...m, isread: true } : m
            ),
          }
          : prev
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    await deleteInboxMessage({ id: deleteId });
    setData((prev) =>
      prev
        ? { details: prev.details.filter((m) => m.id !== deleteId) }
        : prev
    );
    setDeleteId(null);
    dispatch(markReadInboxMessage());
    toast.success(localization.message_delete_notify);
  };

  const handleReadAll = async () => {
    setIsProcessing(true);
    await readAllInboxMessages({ isAllRead: true });
    dispatch(markReadAllInboxMessages());
    setPageNo(0);
    setHasMore(true);
    setData(null);
    await fetchData(true);
    setIsProcessing(false);
  };

  if (loading) {
    return (
      <div className="bg-black flex items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      <div className="lg:max-w-[90%] mx-auto">

        {/* READ ALL */}
        {data?.details.length ? (
          <div className="px-4 md:px-6 py-3 flex justify-end">
            <Button
              disabled={isProcessing}
              onClick={handleReadAll}
              className="flex items-center gap-1 text-sm text-gray-300 hover:text-white transition"
            >
              <img src={"/icons/ic-allread.svg"} className="w-5 h-5" alt="read all" />
              <span>
                {isProcessing ? "Processing..." : localization.read_all}
              </span>
            </Button>
          </div>
        ) : null}

        <InfiniteScroll
          onLoadMore={fetchData}
          hasMore={hasMore}
          isLoading={isLoadingMore}
        >
          <div className="space-y-0 md:space-y-3 px-0 md:px-2">
            {data?.details.map((item) => {
              const expanded = expandedId === item.id;

              return (
                <div key={item.id} className="bg-black">
                  {!expanded && (
                    <div
                      onClick={() => handleExpand(item)}
                      className="
                         px-4 py-3 md:px-6 md:py-4
                         cursor-pointer
                         hover:bg-[#0d0d0d]
                         border-t border-[#2a2a2a]
                         md:rounded-lg md:border
                         md:hover:shadow-md md:hover:shadow-black/40
                         transition">
                      <div className="flex items-start md:items-center justify-between gap-3 mb-1">
                        <h3 className="text-sm font-semibold text-white">
                          {isHindi
                            ? item.hindimessagetitle
                            : item.englishmessagetitle}
                        </h3>

                        {!item.isread && (
                          <span className="px-2 py-0.5 bg-[#056350] text-white text-xs rounded-full">
                            {localization.new}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-300 line-clamp-2 md:line-clamp-3">
                        {isHindi
                          ? decodeHtml(item.hindimessage)
                          : decodeHtml(item.englishmessage)}
                      </p>

                      <div className="mt-2 text-xs text-gray-500">
                        {formatUtcToLocalDateTime(item.created)}
                      </div>
                    </div>
                  )}

                  {expanded && (
                    <div
                      onClick={() => handleExpand(item)}
                      className="
                                                px-4 py-3 md:px-6 md:py-5
                                                bg-[#0d0d0d]
                                                border-t border-[#2a2a2a]
                                                md:rounded-lg md:border
                                                animate-in fade-in slide-in-from-top-2 duration-200
                                            "
                    >
                      <div className="flex items-start md:items-center justify-between gap-3 mb-2">
                        <h3 className="text-sm font-semibold text-white">
                          {isHindi
                            ? item.hindimessagetitle
                            : item.englishmessagetitle}
                        </h3>

                        {!item.isread && (
                          <span className="px-2 py-0.5 bg-[#056350] text-white text-xs rounded-full">
                            {localization.new}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed md:leading-loose">
                        {isHindi
                          ? decodeHtml(item.hindimessage)
                          : decodeHtml(item.englishmessage)}
                      </p>

                      <div className="flex items-center justify-between mt-3 md:mt-4">
                        <span className="text-xs text-gray-500">
                          {formatUtcToLocalDateTime(item.created)}
                        </span>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(item.id);
                          }}
                          className="hover:opacity-80 transition"
                        >
                          <img
                            src={"/icons/ic-delete.svg"}
                            className="w-5 h-5 md:w-6 md:h-6"
                            alt="delete"
                          />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {isLoadingMore && (
            <div className="flex justify-center py-6">
              <div className="animate-spin h-6 w-6 border-2 border-cyan-500 border-t-transparent rounded-full" />
            </div>
          )}
        </InfiniteScroll>
      </div>

      {/* DELETE CONFIRMATION */}
      {deleteId && (
        <ConfirmationModal
          isOpen
          title={localization.confirm_delete}
          description={localization.sure_you_want_delete_this_message}
          icon={"/icons/ic-info.svg"}
          confirmText="Yes"
          cancelText="No"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}
